import React, {useContext, useEffect, useState} from 'react';
import axios from 'axios';
import {useNavigate} from "react-router-dom";
import {ProgressSpinner} from "primereact/progressspinner";
import {useAuth} from "../useAuth";

// The URL to create a new organization in ObjectsGrid
const objectsGridCreateOrgUrl = process.env.REACT_APP_OBJECTS_GRID_BASE_URL + '/organizationaccounts/';
console.log('objectsGridCreateOrgUrl:', objectsGridCreateOrgUrl);

// The URL to exchange the Google token for an ObjectsGrid token
const objectsGridUserAccountUrl = process.env.REACT_APP_OBJECTS_GRID_BASE_URL + '/oauth2/token/useraccount';
console.log('objectsGridUserAccountUrl:', objectsGridUserAccountUrl);

// The URL used by the popup window (or redirect) to Google authentication
const googleAuthUrl = process.env.REACT_APP_GOOGLE_AUTH_URL;
console.log('googleAuthUrl:', googleAuthUrl);

/*
    This component is the landing page for the user when they are redirected back from the Google login page. At this point, the user has been
    through the authentication process with Google and the URL contains an access code that can be exchanged for tokens. Since we now have
    the users Google Profile, we can either use this to create a new Org in ObjectsGrid or use an existing Org ID to get a token for the user. Once
    the user has an ObjectsGrid token, they can make API calls to ObjectsGrid.
 */
const SocialRedirectLanding = () => {

    const navigate = useNavigate();

    const {
        setOgToken,
        setOgOrgId,
        ogAccessPoint,
        setOgAccessPoint,
        googleIdToken,
        setOgRefreshToken,
        setErrorMessage
    } = useAuth();

    // Pick up all of the user input values from localStorage from before we were redirected back by Google
    const [registrationType] = useState(() => localStorage.getItem('registrationType') || '');
    const [orgName] = useState(() => localStorage.getItem('orgName') || '');
    const [orgDescription] = useState(() => localStorage.getItem('orgDescription') || '');
    const [monthlyQuota] = useState(() => localStorage.getItem('monthlyQuota') || '');
    const [countryCode] = useState(() => localStorage.getItem('countryCode') || '');
    const [accessPoint] = useState(() => localStorage.getItem('accessPoint') || '');
    const [userInputOrgAccountId] = useState(() => localStorage.getItem('userInputOrgAccountId') || '');

    // Trigger this effect when the component mounts.
    useEffect(() => {
        console.log("Google Id Token:", googleIdToken);
        console.log("registrationType: ", registrationType);
        console.log("Org Name: ", orgName);
        console.log("orgDescription: ", orgDescription);
        console.log("monthlyQuota: ", monthlyQuota);
        console.log("countryCode: ", countryCode);
        console.log("accessPoint: ", accessPoint);
        console.log("userInputOrgAccountId: ", userInputOrgAccountId);

        // Need to copy over the accessPoint that was taken from local storage into the context.
        setOgAccessPoint(accessPoint);

        //getCountryCodes().then(r => console.log("Got the country codes"));
        handleExchangeSocialToken();
    }, []);


    const handleExchangeSocialToken = async () => {

        let timey = Date.now();

        try {

            // Step 1. If this is a new user that doesn't already have an Org registered in ObjectsGrid, we need to
            // create a new Org for them so that we can use the Org ID in the call to exchange the Google token for an
            // ObjectsGrid one. If this is a token exchange for a user on an existing org, use the orgId that the user gave.
            let orgId: React.SetStateAction<string | null>;
            if (registrationType === 'newUserNewOrg') {
                console.log('Creating new org in ObjectsGrid');
                timey = Date.now();
                orgId = await createObjectsGridOrg(googleIdToken);
                console.log('Create new org in ObjectsGrid in ' + (Date.now() - timey) + 'ms');
            } else {
                console.log('Not creating new Org and using existing Org ID', userInputOrgAccountId);
                orgId = userInputOrgAccountId;
            }

            // Set the Organisation Account ID in the context so that we can use it in the rest of the app.
            console.log('org_id: ', orgId);
            if (orgId === null || orgId === '') {
                console.error('OrgId is null or empty. This should not happen. Please check your configuration.');
                return;
            }
            setOgOrgId(orgId);

            // If the access point is set, then use it. If it isn't then just use US for this call. This is done because we don't want the user
            // to have to choose the accessPoint. If they get it wrong, they'll get an error when trying to work with the app. This simplifies
            // the user experience.
            if (!accessPoint) {
                console.log("No accessPoint set - setting to US for the token exchange.");
                setOgAccessPoint('US');

                exchangeGoogleIdTokenForObjectsGridToken(orgId, 'US').then(() => {
                    console.log("Exchange Google ID Token for ObjectsGrid Token completed successfully with access point: US");
                    navigate("/organisation");
                }).catch(() => {
                    console.error("Something went wrong when trying to exchange google token for OG one using accessPoint US. Trying EU.");
                    exchangeGoogleIdTokenForObjectsGridToken(orgId, 'EU').then(() => {
                        console.log("Exchange Google ID Token for ObjectsGrid Token completed successfully");
                        setOgAccessPoint('EU');
                        navigate("/organisation");
                    }).catch(() => {
                        console.error("Something went wrong when trying to exchange google token for OG one - both EU and US access points.");
                        setErrorMessage("No such org: " + orgId);
                        setOgOrgId(null);
                        navigate("/error");
                    });
                });
            } else {
                exchangeGoogleIdTokenForObjectsGridToken(orgId, accessPoint).then(() => {
                    console.log("Exchange Google ID Token for ObjectsGrid Token completed successfully with access point: " + accessPoint);
                    navigate("/organisation");
                }).catch(() => {
                    console.error("Something went wrong when trying to exchange google token for OG one using accessPoint: " + accessPoint);
                });
            }

        } catch (error) {
            console.error("Login process failed:", error);
            setErrorMessage("Login process failed: " + error);
            setOgOrgId(null);
            navigate("/error");
        }
    };

    const createObjectsGridOrg = async (googleToken: string | null) => {
        if (!orgName) throw new Error("Organization Name is required");
        if (!countryCode) throw new Error("Country Code is required");
        if (!accessPoint) throw new Error("Access Point is required");

        console.log("Country Code: ", countryCode);
        console.log("Access Point: ", accessPoint);

        const response = await axios.post(
            objectsGridCreateOrgUrl,
            {
                name: orgName,
                description: !orgDescription ? '' : orgDescription,
                countryCode: countryCode,
                commerceCode: "12345690",
                accessPoint: accessPoint,
                monthlyQuota: Number(monthlyQuota),
                tags: [],
            },
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${googleToken}`,
                    "X-ObjectsGrid-AccessPoint": accessPoint,
                },
            }
        );

        const orgId = response.data.id;
        console.log("objectsGridOrgId", orgId);

        return orgId;
    };

    const exchangeGoogleIdTokenForObjectsGridToken = (orgId: any, accessPointToTry: any) => {
        console.log("Trying to exchange tokens using accessPoint: ", accessPointToTry);

        orgId = orgId.toLowerCase();
        console.log("orgId: ", orgId);

        return new Promise(async (resolve, reject) => {
            try {
                console.log('Exchanging Google ID Token for ObjectsGrid Token');
                let timey = Date.now();

                const response = await axios.post(
                    objectsGridUserAccountUrl,
                    {
                        grant_type: "urn:ietf:params:oauth:grant-type:token-exchange",
                        subject_token: googleIdToken,
                        subject_token_type: "urn:ietf:params:oauth:token-type:id_token",
                        client_id: orgId,
                        "X-ObjectsGrid-AccessPoint": accessPointToTry,
                    },
                    {
                        headers: {
                            "Content-Type": "application/json",
                            "X-ObjectsGrid-AccessPoint": accessPointToTry,
                        },
                    }
                );

                console.log('Completed exchangeGoogleIdTokenForObjectsGridToken in ' + (Date.now() - timey) + 'ms');
                console.log("Response from exchangeGoogleIdTokenForObjectsGridToken:", response.data);
                setOgToken(response.data.access_token);
                setOgRefreshToken(response.data.refresh_token);
                resolve(response.data);  // Resolve the Promise with the response data
            } catch (error) {
                console.error('Error exchanging Google ID Token for ObjectsGrid Token:', error);
                reject(error);  // Reject the Promise if there is an error
            }
        });
    };

    return (
        <div>
            <ProgressSpinner/>
        </div>
    );
};

export default SocialRedirectLanding;